import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { roleGuard } from './core/auth/role.guard';
import { permissionGuard } from './core/auth/permission.guard';
import { homeRedirect } from './core/auth/home-redirect';

export const routes: Routes = [
  {
    path: '',
    redirectTo: homeRedirect,
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./features/auth/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent),
  },
  {
    path: 'accesso-negato',
    loadComponent: () =>
      import('./features/auth/access-denied/access-denied.component').then(m => m.AccessDeniedComponent),
  },
  {
    path: '',
    loadComponent: () =>
      import('./layouts/shell/shell.component').then(m => m.ShellComponent),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/live-dashboard/live-dashboard.component').then(m => m.LiveDashboardComponent),
        canActivate: [permissionGuard('can_search_calls')],
      },
      // Legacy dashboard personalizzabile — non più in menu, accessibile via URL
      {
        path: 'dashboard/custom',
        loadComponent: () =>
          import('./features/dashboard/dashboard-list/dashboard-list.component').then(m => m.DashboardListComponent),
      },
      {
        path: 'dashboard/custom/:id',
        loadComponent: () =>
          import('./features/dashboard/dashboard-view/dashboard-view.component').then(m => m.DashboardViewComponent),
      },
      {
        path: 'conversations',
        loadComponent: () =>
          import('./features/conversations/conversation-archive/conversation-archive.component').then(m => m.ConversationArchiveComponent),
        canActivate: [permissionGuard('can_use_chat')],
      },
      {
        // Draft chat: renderizza ConversationDetailComponent con il flag
        // ``draft`` via route data. La conversazione viene persistita solo
        // quando l'utente invia il primo messaggio (vedi send()).
        path: 'conversations/new',
        loadComponent: () =>
          import('./features/conversations/conversation-detail/conversation-detail.component').then(m => m.ConversationDetailComponent),
        canActivate: [permissionGuard('can_use_chat')],
        data: { draft: true },
      },
      {
        path: 'conversations/:id',
        loadComponent: () =>
          import('./features/conversations/conversation-detail/conversation-detail.component').then(m => m.ConversationDetailComponent),
        canActivate: [permissionGuard('can_use_chat')],
      },
      {
        path: 'agent-jobs',
        loadComponent: () =>
          import('./features/agent-jobs/agent-jobs-list/agent-jobs-list.component').then(m => m.AgentJobsListComponent),
        canActivate: [permissionGuard('can_use_chat')],
      },
      {
        path: 'agent-jobs/new',
        loadComponent: () =>
          import('./features/agent-jobs/agent-job-create/agent-job-create.component').then(m => m.AgentJobCreateComponent),
        canActivate: [permissionGuard('can_use_chat')],
      },
      {
        path: 'agent-jobs/:id',
        loadComponent: () =>
          import('./features/agent-jobs/agent-job-detail/agent-job-detail.component').then(m => m.AgentJobDetailComponent),
        canActivate: [permissionGuard('can_use_chat')],
      },
      {
        path: 'artifacts',
        loadComponent: () =>
          import('./features/artifacts/artifacts-list/artifacts-list.component').then(m => m.ArtifactsListComponent),
        canActivate: [permissionGuard('can_use_chat')],
      },
      {
        path: 'my-messages',
        loadComponent: () =>
          import('./features/messages/my-messages.component').then(m => m.MyMessagesComponent),
      },
      {
        path: 'search/calls',
        loadComponent: () =>
          import('./features/search/call-search/call-search.component').then(m => m.CallSearchComponent),
        canActivate: [permissionGuard('can_search_calls')],
      },
      {
        path: 'search/semantic',
        loadComponent: () =>
          import('./features/search/semantic-search/semantic-search.component').then(m => m.SemanticSearchComponent),
        canActivate: [permissionGuard('can_semantic_search')],
      },
      {
        path: 'search/geo',
        loadComponent: () =>
          import('./features/geo-search/geo-search.component').then(m => m.GeoSearchComponent),
        canActivate: [permissionGuard('can_geo_search')],
      },
      {
        path: 'collections',
        loadComponent: () =>
          import('./features/collections/collection-list/collection-list.component').then(m => m.CollectionListComponent),
        canActivate: [permissionGuard('can_add_to_collections')],
      },
      {
        path: 'collections/:id',
        loadComponent: () =>
          import('./features/collections/collection-detail/collection-detail.component').then(m => m.CollectionDetailComponent),
        canActivate: [permissionGuard('can_add_to_collections')],
      },
      {
        path: 'stats',
        loadComponent: () =>
          import('./features/stats/stats-shell/stats-shell.component').then(m => m.StatsShellComponent),
        canActivate: [permissionGuard('can_search_calls')],
        children: [
          { path: 'traffic',  loadComponent: () => import('./features/stats/call-traffic/call-traffic.component').then(m => m.CallTrafficComponent) },
          { path: 'content',  loadComponent: () => import('./features/stats/content-analysis/content-analysis.component').then(m => m.ContentAnalysisComponent) },
          { path: 'channels', loadComponent: () => import('./features/stats/channel-occupancy/channel-occupancy.component').then(m => m.ChannelOccupancyComponent) },
          { path: 'usage',    loadComponent: () => import('./features/stats/app-usage/app-usage.component').then(m => m.AppUsageComponent), canActivate: [roleGuard('admin')] },
          { path: 'ai',       loadComponent: () => import('./features/stats/ai-agents/ai-agents.component').then(m => m.AiAgentsComponent), canActivate: [roleGuard('admin')] },
          { path: '', redirectTo: 'traffic', pathMatch: 'full' },
        ],
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/profile.component').then(m => m.ProfileComponent),
      },
      {
        path: 'admin',
        canActivate: [roleGuard('admin')],
        loadComponent: () =>
          import('./features/admin/admin-shell/admin-shell.component').then(m => m.AdminShellComponent),
        children: [
          { path: '', loadComponent: () => import('./features/admin/admin-overview/admin-overview.component').then(m => m.AdminOverviewComponent) },
          { path: 'users',         loadComponent: () => import('./features/admin/users/users.component').then(m => m.UsersComponent) },
          { path: 'roles',         loadComponent: () => import('./features/admin/roles/roles.component').then(m => m.RolesComponent) },
          { path: 'permissions',   loadComponent: () => import('./features/admin/permissions/permissions.component').then(m => m.PermissionsComponent) },
          { path: 'entities',      loadComponent: () => import('./features/admin/entities/entities.component').then(m => m.EntitiesComponent) },
          { path: 'recorders',    loadComponent: () => import('./features/admin/recorders/recorders.component').then(m => m.RecordersComponent) },
          { path: 'channels',     loadComponent: () => import('./features/admin/channels/channels.component').then(m => m.ChannelsComponent) },
          // Le zone geografiche sono admin: non allargano ciò che un utente
          // vede, gli danno solo un'area con un nome dentro cui cercare.
          { path: 'geo-zones',    loadComponent: () => import('./features/admin/geo-zones/geo-zones.component').then(m => m.GeoZonesComponent) },
          // Campi custom e Manutenzione sono super-user: i primi cambiano ciò che
          // *ogni* utente vede nel dettaglio delle chiamate, la seconda ha già
          // un'API `require_role(SUPER_USER)` e senza guardia un admin arrivava
          // alla pagina per poi trovarci un errore.
          { path: 'custom-fields', loadComponent: () => import('./features/admin/custom-fields/custom-fields.component').then(m => m.CustomFieldsComponent),
                                   canActivate: [roleGuard('super-user')] },
          { path: 'audit',         loadComponent: () => import('./features/admin/audit/audit.component').then(m => m.AuditComponent) },
          // Logs cover the whole installation: they cannot be cut down to
          // an entity admin's scope.
          { path: 'logs',          loadComponent: () => import('./features/admin/logs/logs.component').then(m => m.LogsComponent),
                                   canActivate: [roleGuard('super-user')] },
          { path: 'maintenance',   loadComponent: () => import('./features/admin/maintenance/maintenance.component').then(m => m.MaintenanceComponent),
                                   canActivate: [roleGuard('super-user')] },
          { path: 'config',        loadComponent: () => import('./features/admin/config/config.component').then(m => m.ConfigComponent),
                                   canActivate: [roleGuard('super-user')] },
          { path: 'messaging',     loadComponent: () => import('./features/admin/messaging/messaging.component').then(m => m.MessagingComponent) },
        ],
      },
    ],
  },
  { path: '**', redirectTo: homeRedirect },
];
